<script lang="ts">
const __DsBlock = {
	type: 'display',
	name: '$t:blocks.display_color_value.name',
	description: '$t:blocks.display_color_value.description',
	category: 'display',
	hidden: true,

	fieldBinding: { supportedTypes: ['color'] },
}
</script>
<script setup lang="ts">
import type { DisplayBlockProps } from '#layers/blocks/types/display-block';
import { ColorTranslator } from 'colortranslator';
import { computed, ref } from 'vue';

import { defineBlock } from '#layers/block-engine/public';
import { useTouchScreen } from '#layers/ui/public';

export interface DisplayColorValueProps extends DisplayBlockProps<string | null> {}

const { value } = defineProps<DisplayColorValueProps>();

;

const isTouchScreen = useTouchScreen();
const tooltipOpen = ref(false);
const visibleHexValue = computed(() => {
	if (!value) return null;

	try {
		const hexadecimal = ColorTranslator.toHEXA(value);
		return hexadecimal.endsWith('FF') ? hexadecimal.slice(0, -2) : hexadecimal;
	}
	catch {
		return null;
	}
});
const intrinsicWidthClass = computed(() => {
	if (visibleHexValue.value === null) return '[contain-intrinsic-inline-size:0.75rem]';

	return visibleHexValue.value.length === 7
		? '[contain-intrinsic-inline-size:calc(7ch+1.25rem+1px)]'
		: '[contain-intrinsic-inline-size:calc(9ch+1.25rem+1px)]';
});

function toggleMobileTooltip() {
	if (isTouchScreen.value) tooltipOpen.value = !tooltipOpen.value;
}
</script>

<template>
	<DDisplayValueEmpty v-if="value === null || value === undefined" />
	<span
		v-else
		class="@container/color-value flex w-full min-w-0 items-center font-mono text-sm"
		:class="intrinsicWidthClass"
	>
		<DTooltip
			v-model:open="tooltipOpen"
			:text="value"
			:ui="{ content: 'font-mono' }"
			:disable-closing-trigger="isTouchScreen"
		>
			<button
				v-if="isTouchScreen"
				type="button"
				class="-m-2 inline-flex shrink-0 items-center justify-center rounded-sm p-2"
				data-testid="color-value-trigger"
				:aria-label="value"
				@keydown.enter.stop
				@keydown.space.stop
				@click.stop="toggleMobileTooltip"
			>
				<DColorDot :color="value" size="sm" />
			</button>
			<DColorDot v-else :color="value" size="sm" />
		</DTooltip>
		<span v-if="!isTouchScreen" class="sr-only">
			{{ value }}
		</span>

		<!-- Six/eight-digit hex + the 0.75rem dot + the 0.5rem gap. -->
		<span
			v-if="visibleHexValue"
			class="ms-2 shrink-0 whitespace-nowrap"
			:class="visibleHexValue.length === 7
				? '@max-[calc(7ch+1.25rem)]/color-value:hidden'
				: '@max-[calc(9ch+1.25rem)]/color-value:hidden'"
			data-testid="color-value-text"
			aria-hidden="true"
		>
			{{ visibleHexValue }}
		</span>
	</span>
</template>
